import {
  CountryCode,
  isValidPhoneNumber,
  validatePhoneNumberLength,
  parsePhoneNumberWithError,
  PhoneNumber,
} from 'libphonenumber-js';
import { isEmpty } from 'validator';

// Types
import {
  PhoneValidationRules,
  PhoneAdditionalDetails,
} from '../../types/controls/phone';
import { ValidationMessages } from '../../types/contexts/formProvider';

// Static Content
import content from '../../content/content.json';

type ValidateRequest = {
  value: string;
  countryCode: CountryCode;
  ruleSet?: PhoneValidationRules;
  validationMessages: ValidationMessages;
};

export const validate = (request: ValidateRequest): string | undefined => {
  if (request.ruleSet?.required && isEmpty(request.value)) {
    return request.validationMessages.required ?? content.required;
  }

  return validateNumber(request);
};

export const validateNumber = ({
  value,
  countryCode,
  validationMessages,
}: ValidateRequest) => {
  if (isEmpty(value)) {
    return undefined;
  }

  const defaultErrorMessage =
    validationMessages.contactNumberInvalid ?? content.contactNumberInvalid;

  try {
    const { country } = parsePhoneNumberWithError(value, countryCode);

    if (country !== countryCode) {
      return defaultErrorMessage;
    }
  } catch {
    return defaultErrorMessage;
  }

  const validateLength = validatePhoneNumberLength(value, countryCode);

  if (validateLength !== undefined) {
    const response = {
      NOT_A_NUMBER: () => defaultErrorMessage,
      INVALID_LENGTH: () => defaultErrorMessage,
      INVALID_COUNTRY: () => defaultErrorMessage,
      TOO_LONG: () =>
        validationMessages.contactNumberTooLong ?? content.contactNumberTooLong,
      TOO_SHORT: () =>
        validationMessages.contactNumberTooShort ??
        content.contactNumberTooShort,
    }[validateLength]();

    return response;
  }

  if (!isValidPhoneNumber(value, countryCode)) {
    return defaultErrorMessage;
  }

  return undefined;
};

type ParsePhoneNumberRequest = {
  value: string;
  countryCode: CountryCode;
  validationMessage: string | undefined;
};

export const parsePhoneNumber = ({
  value,
  countryCode,
  validationMessage,
}: ParsePhoneNumberRequest): PhoneAdditionalDetails | undefined => {
  if (validationMessage || isEmpty(value)) {
    return undefined;
  }

  let parsedNumber: PhoneNumber | undefined = undefined;

  try {
    parsedNumber = parsePhoneNumberWithError(value, countryCode);
  } catch {
    return undefined;
  }

  return {
    e164: parsedNumber.number,
    callingCode: `+${parsedNumber.countryCallingCode}`,
  };
};
